// A source focused in the right panel: first the source itself, as it is,
// with every quoted passage marked (pen E-04, "the source beside"), then
// WHERE it is used ("Used in": every statement its claims back, grouped by
// material, plus the chat replies), what the agent knows from it, and its
// summary.
//
// Arriving from one statement (a gutter anchor) puts that statement and its
// passage on top, the exact quote marked, under the one named step back
// ("‹ Back to <material>"). A statement in "Used in" opens the material at
// it; a long list folds per material ("Show all N").
// A source outside the chat's sources offers "Attach to the chat".
//
// Projection in (rightPanel/sourceFocus.ts), intents out. Local state: which
// materials' claim lists are unfolded (view-ephemeral). The text is read
// through the port seam.

import { Remote } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Button } from "../../../components/Button/Button.js";
import {
    type Claim,
    type ClaimIntents,
    ClaimList,
} from "../../../components/ClaimRow/ClaimRow.js";
import { Icon } from "../../../components/Icon/Icon.js";
import { focusPagesOf } from "../../../components/Navigation/focusTarget.js";
import type {
    FocusTarget,
    MaterialRef,
    SourceRef,
} from "../../../components/Navigation/types.js";
import {
    PanelSourceView,
    type SourceViewModel,
} from "../../../components/PanelSourceView/PanelSourceView.js";
import type { StatementSource } from "../../../components/QualitySummary/QualitySummary.js";
import { SourceReadStatus } from "../../../components/SourceReadStatus/SourceReadStatus.js";
import { SourceSummary } from "../../../components/SourceSummary/SourceSummary.js";
import { Spinner } from "../../../components/Spinner/Spinner.js";
import { Text } from "../../../components/Text/Text.js";
import type { ExternalError } from "../../externalError.js";
import type { StatementOrigin } from "../../frame/types.js";
import type { LoadedSource } from "../../ports/resource.js";
import type { AppPorts } from "../../ports.js";
import type { SourceReadStatus as ReadStatus } from "../../sourceReadStatus.js";
import { usePortLoad } from "../ResourceDetail.js";
import { type ChatWorld, isUnused, replyIdsOf } from "./home.js";
import {
    type Arrival,
    arrivalOf,
    canAttach,
    foldStatements,
    markQuotes,
    passageOf,
    quotesOf,
    runsOf,
    usageIn,
} from "./sourceUsage.js";

export type SourceFocusProps = {
    readonly source: SourceRef;
    readonly target: FocusTarget | undefined;
    readonly origin: StatementOrigin | undefined;
    readonly world: ChatWorld;
    // How far reading it in has come (FR-3.3/3.4/3.6); absent ⇒ none shown.
    readonly readStatus?: ReadStatus | undefined;
    readonly loadSource: AppPorts["loadSource"];
    // Open a material at one of its claims.
    readonly onShowStatement: (material: MaterialRef, claimId: string) => void;
    readonly onShowReplies: (entryIds: readonly string[]) => void;
    // Ground the source in this chat (absent when it can't be attached).
    readonly onAttach?: (source: SourceRef) => void;
    // Re-upload a source whose stored content is missing; `onRestored` re-reads.
    readonly onReupload?: (source: SourceRef, onRestored: () => void) => void;
    // The knowledge bank's claims from this source (FR-13.9); absent ⇒ the
    // section is not shown.
    readonly claims?: {
        readonly list: readonly Claim[];
        readonly intents: ClaimIntents;
    };
};

// One passage with the exact quote marked.
const QuotedPassage = ({
    text,
    quote,
}: {
    readonly text: string;
    readonly quote: string;
}): ReactNode => (
    <p className="bilda-rp-quote">
        {runsOf(text, quote).map((run, i) =>
            run.type === "quoted" ? (
                // biome-ignore lint/suspicious/noArrayIndexKey: runs are positional and stable per passage
                <mark key={i} className="bilda-rp-mark">
                    {run.text}
                </mark>
            ) : (
                // biome-ignore lint/suspicious/noArrayIndexKey: runs are positional and stable per passage
                <span key={i}>{run.text}</span>
            ),
        )}
    </p>
);

const ArrivalCard = ({
    arrival,
    source,
    passage,
}: {
    readonly arrival: Arrival;
    readonly source: SourceRef;
    readonly passage: string | undefined;
}): ReactNode => {
    const { t } = useLingui();
    const materialTitle = arrival.material.title;
    const where =
        arrival.row.page === undefined
            ? t`Backed by ${source.title}`
            : t`Backed by ${source.title}, p. ${arrival.row.page}`;
    return (
        <div className="bilda-rp-arrival">
            <Text as="p" size="xs" tone="faint">
                {t`Claim in ${materialTitle}`}
            </Text>
            <Text as="p" size="sm">
                {arrival.row.statement}
            </Text>
            <Text as="p" size="xs" tone="faint">
                {where}
            </Text>
            <QuotedPassage
                text={passage ?? arrival.row.quote}
                quote={arrival.row.quote}
            />
        </div>
    );
};

const pageOf = (row: StatementSource): string =>
    row.page === undefined ? "" : String(row.page);

const UsedIn = ({
    world,
    source,
    arrival,
    onShowStatement,
    onShowReplies,
}: {
    readonly world: ChatWorld;
    readonly source: SourceRef;
    readonly arrival: Arrival | undefined;
    readonly onShowStatement: SourceFocusProps["onShowStatement"];
    readonly onShowReplies: SourceFocusProps["onShowReplies"];
}): ReactNode => {
    const { t } = useLingui();
    const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(new Set());
    const usage = usageIn(world, source);
    const replies = replyIdsOf(usage);
    return (
        <section className="bilda-rp-section" aria-label={t`Used in`}>
            <Text as="p" size="sm" weight="semibold">
                {t`Used in`}
            </Text>
            {isUnused(usage) ? (
                <Text as="p" size="sm" tone="dim">
                    {t`Not used yet. No material and no reply in the chat cites this source.`}
                </Text>
            ) : null}
            {usage.inMaterials.map((use) => {
                const folded = foldStatements(
                    use.rows,
                    unfolded.has(use.material.id),
                );
                const total = use.rows.length;
                return (
                    <div key={use.material.id} className="bilda-rp-usegroup">
                        <span className="bilda-rp-usegroup__title">
                            <Icon name="material" size="xs" />
                            <Text
                                as="span"
                                size="xs"
                                weight="medium"
                                className="bilda-rp-title"
                            >
                                {use.material.title}
                            </Text>
                            <Text as="span" size="xs" tone="faint">
                                {t`${plural(total, {
                                    one: "# place",
                                    other: "# places",
                                })}`}
                            </Text>
                        </span>
                        <ul className="bilda-rp-list bilda-rp-list--tight">
                            {folded.shown.map((row) => (
                                <li key={row.statementId}>
                                    <button
                                        type="button"
                                        className="bilda-rp-row bilda-rp-claim"
                                        aria-current={
                                            arrival?.row.statementId ===
                                                row.statementId &&
                                            arrival.material.id ===
                                                use.material.id
                                                ? "location"
                                                : undefined
                                        }
                                        onClick={() =>
                                            onShowStatement(
                                                use.material,
                                                row.statementId,
                                            )
                                        }
                                    >
                                        <Text as="span" size="sm">
                                            {row.statement}
                                        </Text>
                                        {row.page === undefined ? null : (
                                            <Text
                                                as="span"
                                                size="xs"
                                                tone="faint"
                                                className="bilda-rp-claim__page"
                                            >
                                                {t`p. ${pageOf(row)}`}
                                            </Text>
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                        {folded.hidden > 0 ? (
                            <button
                                type="button"
                                className="bilda-rp-row bilda-rp-more"
                                onClick={() =>
                                    setUnfolded(
                                        new Set([...unfolded, use.material.id]),
                                    )
                                }
                            >
                                <Text as="span" size="xs">
                                    {t`Show all ${total}`}
                                </Text>
                            </button>
                        ) : null}
                    </div>
                );
            })}
            {replies.length === 0 ? null : (
                <div className="bilda-rp-usegroup">
                    <span className="bilda-rp-usegroup__title">
                        <Icon name="chat" size="xs" />
                        <Text as="span" size="xs" weight="medium">
                            {t`Replies in the chat`}
                        </Text>
                    </span>
                    <ul className="bilda-rp-list bilda-rp-list--tight">
                        {usage.inChat.map((r) => (
                            <li key={r.citation.id}>
                                <button
                                    type="button"
                                    className="bilda-rp-row bilda-rp-reply"
                                    onClick={() => onShowReplies([r.entryId])}
                                >
                                    <Text as="span" size="xs" tone="dim">
                                        [{r.citation.index}]
                                    </Text>
                                    <Text as="span" size="sm" tone="dim">
                                        {r.citation.quote === undefined
                                            ? r.citation.sourceTitle
                                            : t`“${r.citation.quote}”`}
                                    </Text>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
};

const modelOf = (
    loaded: LoadedSource,
    title: string,
    quotes: readonly string[],
): SourceViewModel => ({
    type: "source",
    meta: {
        title,
        kind: loaded.meta.kind,
        filename: loaded.meta.filename,
    },
    content: markQuotes(loaded.content, quotes),
    analysis: { type: "ready" },
});

export const SourceFocus = ({
    source,
    target,
    origin,
    world,
    readStatus,
    loadSource,
    onShowStatement,
    onShowReplies,
    onAttach,
    onReupload,
    claims,
}: SourceFocusProps): ReactNode => {
    const { t } = useLingui();
    const { load, retry } = usePortLoad(loadSource, source.id);
    const arrival = arrivalOf(world, source, origin);
    // The passages the chat cites, plus the one the panel was opened at (a
    // goal's requirement words or a term's definition).
    const quotes = [
        ...quotesOf(usageIn(world, source)),
        ...(target?.quote === undefined || target.quote.length === 0
            ? []
            : [target.quote]),
    ];
    const reader = (model: SourceViewModel): ReactNode => (
        <PanelSourceView
            model={model}
            focusPages={focusPagesOf(target)}
            onReupload={
                onReupload === undefined
                    ? undefined
                    : () => onReupload(source, retry)
            }
        />
    );
    const loaded = Remote.isLoaded(load) ? load.value : undefined;
    const passage =
        arrival === undefined || loaded === undefined
            ? undefined
            : passageOf(loaded.content, arrival.row.quote);
    return (
        <div className="bilda-rp-lensbody">
            <SourceReadStatus status={readStatus} />
            {arrival === undefined ? null : (
                <ArrivalCard
                    arrival={arrival}
                    source={source}
                    passage={passage}
                />
            )}
            {Remote.match<LoadedSource, ExternalError, ReactNode>({
                notAsked: () => <Spinner label={t`Loading file…`} />,
                loading: () => <Spinner label={t`Loading file…`} />,
                reloading: (s) => reader(modelOf(s, source.title, quotes)),
                loaded: (s) => reader(modelOf(s, source.title, quotes)),
                failed: () => reader({ type: "not-found" }),
            })(load)}
            {onAttach !== undefined && canAttach(world, source) ? (
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onAttach(source)}
                >
                    {t`Attach to the chat`}
                </Button>
            ) : null}
            <UsedIn
                world={world}
                source={source}
                arrival={arrival}
                onShowStatement={onShowStatement}
                onShowReplies={onShowReplies}
            />
            {claims === undefined ? null : (
                <section
                    className="bilda-rp-section"
                    aria-label={t`What the agent knows from this source`}
                >
                    <Text as="p" size="sm" weight="semibold">
                        {t`What the agent knows from this source`}
                    </Text>
                    <ClaimList
                        claims={claims.list}
                        intents={claims.intents}
                        empty={t`No claims in the knowledge bank from this source yet.`}
                    />
                </section>
            )}
            <SourceSummary summary={loaded?.summary} />
        </div>
    );
};
